import { Link } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import { motion, useInView, useMotionValue, useTransform, animate, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Sparkles, BookOpen,
  Shield, Zap, Users, Award, Calendar, Quote, Star, ShieldCheck, Search, ClipboardList,
  Send, GraduationCap, CheckCircle2, XCircle, Plus,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { useCms } from "@/lib/cms";
import TrainersSection from "@/components/TrainersSection";
import AchievementsSection from "@/components/AchievementsSection";
import { trainerInitials, resolveTrainerImage } from "@/lib/trainers";

// ── Typography helper ─────────────────────────────────────────────────────────
// Reads home_typography[key] and returns an inline style object.
// Falls back gracefully — if no CMS override, returns {} so hardcoded Tailwind
// classes remain in effect. This means zero visual regression on first deploy.
function ts(home, key, extra) {
  const t = home?.home_typography?.[key];
  if (!t) return extra || {};
  const s = {};
  if (t.fontFamily) s.fontFamily = t.fontFamily === "Playfair Display" ? "'Playfair Display', serif"
    : t.fontFamily === "Inter" ? "'Inter', sans-serif"
    : t.fontFamily === "Poppins" ? "'Poppins', sans-serif"
    : t.fontFamily === "Montserrat" ? "'Montserrat', sans-serif"
    : t.fontFamily === "Open Sans" ? "'Open Sans', sans-serif"
    : t.fontFamily === "Lato" ? "'Lato', sans-serif"
    : t.fontFamily === "Cormorant Garamond" ? "'Cormorant Garamond', serif"
    : t.fontFamily === "Georgia" ? "Georgia, serif"
    : t.fontFamily;
  if (t.fontSize) s.fontSize = `${t.fontSize}px`;
  if (t.fontWeight) s.fontWeight = t.fontWeight;
  if (t.fontStyle && t.fontStyle !== "normal") s.fontStyle = t.fontStyle;
  if (t.letterSpacing) s.letterSpacing = `${t.letterSpacing}em`;
  if (t.lineHeight) s.lineHeight = t.lineHeight;
  if (t.textTransform && t.textTransform !== "none") s.textTransform = t.textTransform;
  if (t.textDecoration && t.textDecoration !== "none") s.textDecoration = t.textDecoration;
  if (t.textAlign && t.textAlign !== "left") s.textAlign = t.textAlign;
  if (t.color) s.color = t.color;
  return { ...extra, ...s };
}


// --- New palette (this page only) — reverse-engineered from the rebrand spec.
// Not added to tailwind.config.js or index.css: kept local to this file so no other
// page inherits it, per the "Home page only" scope decision.
const C = {
  blue: "#3B68E7",
  teal: "#45B8AC",
  gold: "#DDA756",
  ink: "#1A202C",
  muted: "#64748B",
  surface: "#F8FAFC",
};

const DEFAULT_STATS = [
  {
    id: "kpi_1", k: "9.5K+", v: "Educators Empowered",
    label: "Educators Empowered", value: "9500", prefix: "", suffix: "+",
    icon: "Users", iconColor: "#FFFFFF", iconBg: "rgba(255,255,255,0.2)",
    gradient: `linear-gradient(135deg, #3B68E7E6 0%, #3B68E799 100%)`,
    visible: true, order: 0,
    counterEnabled: true, counterDuration: 1.8, counterDelay: 0,
    counterDecimals: 0, counterSeparator: ",", counterStart: 0,
    numberColor: "#FFFFFF", labelColor: "rgba(255,255,255,0.9)",
    numberFontSize: 24, numberFontWeight: 600, labelFontSize: 11, labelFontWeight: 400,
  },
  {
    id: "kpi_2", k: "120+", v: "Schools Transforming",
    label: "Schools Transforming", value: "120", prefix: "", suffix: "+",
    icon: "Award", iconColor: "#FFFFFF", iconBg: "rgba(255,255,255,0.2)",
    gradient: `linear-gradient(135deg, #45B8ACE6 0%, #45B8AC99 100%)`,
    visible: true, order: 1,
    counterEnabled: true, counterDuration: 1.8, counterDelay: 0.15,
    counterDecimals: 0, counterSeparator: ",", counterStart: 0,
    numberColor: "#FFFFFF", labelColor: "rgba(255,255,255,0.9)",
    numberFontSize: 24, numberFontWeight: 600, labelFontSize: 11, labelFontWeight: 400,
  },
  {
    id: "kpi_3", k: "4.8", v: "Satisfaction Rating",
    label: "Satisfaction Rating", value: "4.8", prefix: "", suffix: "★",
    icon: "Star", iconColor: "#FFFFFF", iconBg: "rgba(255,255,255,0.2)",
    gradient: `linear-gradient(135deg, #DDA756E6 0%, #DDA75699 100%)`,
    visible: true, order: 2,
    counterEnabled: true, counterDuration: 1.8, counterDelay: 0.3,
    counterDecimals: 1, counterSeparator: ",", counterStart: 0,
    numberColor: "#FFFFFF", labelColor: "rgba(255,255,255,0.9)",
    numberFontSize: 24, numberFontWeight: 600, labelFontSize: 11, labelFontWeight: 400,
  },
];

const DEFAULT_PARTNERS = ["Delhi Public School", "Bombay Scottish", "St. Mary's", "Vidya Mandir", "Heritage School", "The Cathedral School", "Modern School", "Bishop Cotton"];

const FALLBACK_HERO_IMAGE = "https://images.pexels.com/photos/7648050/pexels-photo-7648050.jpeg?auto=compress&cs=tinysrgb&w=1200";

// Featured Programs card themes. The Program model has no theme/color field, so per the
// brief's own fallback allowance ("derive theme using existing category or fallback
// mapping"), themes are picked deterministically from category, cycling through the same
// 3 palettes for any unmapped category rather than requiring a backend field.
const PROGRAM_THEMES = [
  { top: "#2A428D", bottom: "#6581CD", iconBg: "rgba(255,255,255,0.18)", iconColor: "#FFFFFF", title: "#FFFFFF", body: "rgba(255,255,255,0.82)", ctaBg: "rgba(255,255,255,0.18)", ctaText: "#FFFFFF" },
  { top: "#93D6CD", bottom: "#EAF6F5", iconBg: "rgba(26,32,44,0.08)", iconColor: "#1A202C", title: "#1A202C", body: "#3F4C5C", ctaBg: "rgba(26,32,44,0.08)", ctaText: "#1A202C" },
  { top: "#E5C587", bottom: "#F9F2E6", iconBg: "rgba(26,32,44,0.08)", iconColor: "#1A202C", title: "#1A202C", body: "#5C5140", ctaBg: "rgba(26,32,44,0.08)", ctaText: "#1A202C" },
];
const CATEGORY_THEME_INDEX = { "STEM": 0, "Pedagogy": 1, "Technology": 0, "Assessment": 2, "Leadership": 2, "Wellbeing": 1 };
const CATEGORY_ICON = { "STEM": Zap, "Pedagogy": BookOpen, "Technology": Zap, "Assessment": ClipboardList, "Leadership": GraduationCap, "Wellbeing": Shield };
const themeFor = (category, index) => PROGRAM_THEMES[CATEGORY_THEME_INDEX[category] ?? (index % PROGRAM_THEMES.length)];
const iconFor = (category) => CATEGORY_ICON[category] || BookOpen;

const WHY_ICON_MAP = {
  BookOpen, Calendar, Award, Users, Shield, Zap, GraduationCap, ClipboardList,
  Send, Search, CheckCircle2, ShieldCheck, Star, Sparkles,
};

// All icons available for KPI badges (superset of WHY_ICON_MAP)
export const KPI_ICON_MAP = {
  Users, Award, Star, Shield, Zap, BookOpen, Calendar, GraduationCap,
  ClipboardList, Send, Search, CheckCircle2, ShieldCheck, Sparkles,
  ArrowUpRight,
};

// Small luminance check so any admin-chosen node color (not just the 5 defaults) gets a
// correctly contrasting icon — white on dark backgrounds, dark on light ones.
const isLightColor = (hex) => {
  if (!hex || hex[0] !== "#") return false;
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) > 160;
};

// Client-side fallback — same pattern as DEFAULT_STATS/DEFAULT_PARTNERS above. Used only
// when home.why_choose_us is missing or empty (e.g. a persisted CMS document saved before
// this field existed); never overwrites real CMS data once it's present.
const DEFAULT_WHY_CHOOSE_US = [
  { icon: "Users", title: "Expert Faculty", description: "Researchers and master teachers, in every classroom.", visible: true },
  { icon: "Shield", title: "Accredited Programs", description: "Recognised credentials your school can stand behind.", visible: true },
  { icon: "CheckCircle2", title: "Peer Community", description: "A growing network of schools raising the bar together.", visible: true },
];

// Client-side fallback for the Training Journey Timeline — same pattern as
// DEFAULT_STATS/DEFAULT_PARTNERS/DEFAULT_WHY_CHOOSE_US above, content matching the backend
// DEFAULT_HOME exactly so a persisted CMS document missing this field still renders
// correctly instead of showing an empty section.
const DEFAULT_TRAINING_JOURNEY = [
  { icon: "Search", title: "Discover", description: "Browse programs built for your subject and grade band.", color: "#2B4380", link: "", button_text: "", visible: true },
  { icon: "ClipboardList", title: "Book", description: "Pick a date. Confirmed for your school in under a minute.", color: "#4C72E8", link: "", button_text: "", visible: true },
  { icon: "Send", title: "Prepare", description: "Agenda, roster, and trainer details shared ahead of time.", color: "#3FB39E", link: "", button_text: "", visible: true },
  { icon: "GraduationCap", title: "Train", description: "Teachers attend on-site with real classroom application.", color: "#C7EBE8", link: "", button_text: "", visible: true },
  { icon: "Award", title: "Certify", description: "Every teacher receives a secure QR-verified digital certificate.", color: "#D59F54", link: "", button_text: "", visible: true },
];


// ── KPI CountUp — supports per-item duration, delay, decimals, prefix, suffix ──
export function KpiCountUp({ kpi }) {
  // New rich schema: value is a raw number, prefix/suffix are separate
  // Legacy schema fallback: k = "12,400+" (value+suffix combined), no prefix field
  const isRich = kpi.value !== undefined && kpi.value !== null && kpi.value !== "";
  const rawValue = isRich ? String(kpi.value) : String(kpi.k || "");
  const match = rawValue.match(/^([\d,.]+)(.*)$/);
  const numStr = match ? match[1] : null;
  const target = numStr ? parseFloat(numStr.replace(/,/g, "")) : null;

  // prefix/suffix from new schema, or extracted from legacy k string
  const prefix = isRich ? (kpi.prefix || "") : "";
  const suffix = isRich ? (kpi.suffix || "") : (match ? match[2] : "");
  const decimals = kpi.counterDecimals ?? (rawValue.includes(".") ? 1 : 0);
  const sep = kpi.counterSeparator ?? ",";
  const duration = kpi.counterDuration ?? 1.8;
  const delay = kpi.counterDelay ?? 0;
  const start = kpi.counterStart ?? 0;
  const counterEnabled = kpi.counterEnabled !== false;

  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(start);
  const display = useTransform(mv, (v) => {
    const n = decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString(undefined, { useGrouping: sep === "," });
    return `${prefix}${n}${suffix}`;
  });
  const [text, setText] = useState(() => `${prefix}${start}${suffix}`);

  useEffect(() => {
    if (target === null || !counterEnabled) {
      // static display — format the number if parseable
      if (target !== null) {
        const formatted = decimals > 0 ? target.toFixed(decimals) : target.toLocaleString();
        setText(`${prefix}${formatted}${suffix}`);
      } else {
        setText(rawValue);
      }
      return;
    }
    if (!inView) return;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      const formatted = decimals > 0 ? target.toFixed(decimals) : target.toLocaleString();
      setText(`${prefix}${formatted}${suffix}`);
      return;
    }
    const timer = delay > 0 ? setTimeout(() => {
      const controls = animate(mv, target, { duration, ease: [0.16, 1, 0.3, 1] });
      const unsub = display.on("change", (v) => setText(v));
      return () => { controls.stop(); unsub(); };
    }, delay * 1000) : null;

    if (delay === 0) {
      const controls = animate(mv, target, { duration, ease: [0.16, 1, 0.3, 1] });
      const unsub = display.on("change", (v) => setText(v));
      return () => { controls.stop(); unsub(); if (timer) clearTimeout(timer); };
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [inView, target, counterEnabled, duration, delay, decimals, prefix, suffix, mv, display, rawValue, start]); // eslint-disable-line react-hooks/exhaustive-deps

  return <span ref={ref}>{text}</span>;
}

// Legacy CountUp kept for any other page usage
function CountUp({ value }) {
  const match = String(value).match(/^([\d,.]+)(.*)$/);
  const target = match ? parseFloat(match[1].replace(/,/g, "")) : null;
  const suffix = match ? match[2] : "";
  const isDecimal = match && match[1].includes(".");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) => (isDecimal ? v.toFixed(1) : Math.round(v).toLocaleString()) + suffix);
  const [text, setText] = useState(target !== null ? `0${suffix}` : String(value));

  useEffect(() => {
    if (target === null || !inView) return;
    const controls = animate(mv, target, { duration: 1.8, ease: [0.16, 1, 0.3, 1] });
    const unsub = display.on("change", (v) => setText(v));
    return () => { controls.stop(); unsub(); };
  }, [inView, target, mv, display]);

  if (target === null) return <span ref={ref}>{value}</span>;
  return <span ref={ref}>{text}</span>;
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Home() {
  const { home } = useCms();
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programsError, setProgramsError] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [tActive, setTActive] = useState(0);
  // Inline certificate verification — same endpoint, same request/response handling as
  // the standalone /verify page (Verify.jsx), just embedded here per this round's brief
  // instead of only linking out to it. Endpoint name and response shape are untouched.
  const [verifyQuery, setVerifyQuery] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [expandedQuotes, setExpandedQuotes] = useState(() => new Set());
  const tScrollerRef = useRef(null);
  const tInteractingRef = useRef(false);
  const tResumeTimerRef = useRef(null);

  useEffect(() => {
    api.get("/programs")
      .then((r) => {
        const all = r.data || [];
        const featured = all.filter((p) => p.featured);
        setPrograms((featured.length ? featured : all).slice(0, 3));
      })
      .catch(() => setProgramsError(true))
      .finally(() => setProgramsLoading(false));
    api.get("/cms/testimonials")
      .then((r) => setTestimonials(r.data || []))
      .catch(() => setTestimonials([]))
      .finally(() => setTestimonialsLoading(false));
  }, []);

  // Testimonial carousel — native horizontal scroll (gives drag/swipe/wheel support for
  // free, no custom pointer-tracking needed) with a requestAnimationFrame loop nudging
  // scrollLeft forward for the "auto-scroll" effect. Both use the same scrollLeft
  // coordinate, so user interaction and the animation never fight each other. The list is
  // rendered twice (same seamless-loop technique as the Partner Schools marquee); once
  // scrollLeft passes one full set's width, it's silently rewound by that exact width so
  // the loop restart is invisible.
  useEffect(() => {
    const el = tScrollerRef.current;
    if (!el || testimonials.length === 0) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;

    let raf;
    const step = () => {
      if (!tInteractingRef.current && el) {
        el.scrollLeft += 0.4;
        const singleSetWidth = el.scrollWidth / 2;
        if (el.scrollLeft >= singleSetWidth) el.scrollLeft -= singleSetWidth;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [testimonials.length]);

  const pauseTCarousel = () => {
    tInteractingRef.current = true;
    if (tResumeTimerRef.current) clearTimeout(tResumeTimerRef.current);
  };
  const resumeTCarouselSoon = () => {
    if (tResumeTimerRef.current) clearTimeout(tResumeTimerRef.current);
    tResumeTimerRef.current = setTimeout(() => { tInteractingRef.current = false; }, 800);
  };

  const onTScroll = () => {
    const el = tScrollerRef.current;
    if (!el || testimonials.length === 0) return;
    const cardStep = el.scrollWidth / 2 / testimonials.length;
    const idx = Math.round((el.scrollLeft % (el.scrollWidth / 2)) / cardStep) % testimonials.length;
    setTActive(idx);
  };

  const scrollTToIndex = (i) => {
    const el = tScrollerRef.current;
    if (!el || testimonials.length === 0) return;
    const cardStep = el.scrollWidth / 2 / testimonials.length;
    pauseTCarousel();
    el.scrollTo({ left: cardStep * i, behavior: "smooth" });
    resumeTCarouselSoon();
  };

  const onTKeyDown = (e) => {
    const el = tScrollerRef.current;
    if (!el) return;
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      pauseTCarousel();
      const cardStep = el.scrollWidth / 2 / Math.max(testimonials.length, 1);
      el.scrollBy({ left: e.key === "ArrowRight" ? cardStep : -cardStep, behavior: "smooth" });
      resumeTCarouselSoon();
    }
  };

  const toggleQuoteExpanded = (id) => {
    setExpandedQuotes((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const doVerifyInline = async () => {
    if (!verifyQuery.trim()) return;
    setVerifyLoading(true);
    try {
      const { data } = await api.get(`/verify/${verifyQuery.trim()}`);
      setVerifyResult(data);
    } catch {
      setVerifyResult({ valid: false });
    }
    setVerifyLoading(false);
  };

  const heroImageRaw = home?.hero_image || FALLBACK_HERO_IMAGE;
  const heroImage = heroImageRaw.startsWith("http") ? heroImageRaw : `${BACKEND}${heroImageRaw}`;
  const stats = (home?.stats && home.stats.length ? home.stats : DEFAULT_STATS);
  const eyebrow = home?.hero_eyebrow || "Now onboarding schools for 2026";
  const h1 = home?.hero_heading_l1 || "Empowering Educators,";
  const h2 = home?.hero_heading_l2 || "Shaping Tomorrow.";
  const desc = home?.hero_description || "Innovative training for modern classrooms — from booking to certification, in one elegant platform.";
  const ctaPrimary = { text: home?.primary_cta_text || "Join Training", link: home?.primary_cta_link || "/book" };
  const ctaSecondary = { text: home?.secondary_cta_text || "Explore Programs", link: home?.secondary_cta_link || "/programs" };
  const partners = (home?.partners && home.partners.length ? home.partners : DEFAULT_PARTNERS);
  const whyChooseUsRaw = (home?.why_choose_us && home.why_choose_us.length ? home.why_choose_us : DEFAULT_WHY_CHOOSE_US);
  const whyChooseUsItems = whyChooseUsRaw.filter((f) => f.visible !== false);
  const trainingJourneyRaw = (home?.training_journey && home.training_journey.length ? home.training_journey : DEFAULT_TRAINING_JOURNEY);
  const trainingJourneySteps = trainingJourneyRaw.filter((s) => s.visible !== false);
  const journeyHeading = home?.training_journey_heading || "Training Journey Timeline";
  const journeySubtitle = home?.training_journey_subtitle || "Follow a simple journey from discovering programs to earning certificates.";
  const verifyCtaHeading = home?.verify_cta_heading || "Certificate Verification";
  const verifyCtaDescription = home?.verify_cta_description || "Verify certificates instantly using the unique certificate number or QR code issued by our organization.";
  const verifyCtaActive = home?.verify_cta_active !== false;
  const testimonialsHeading = home?.testimonials_heading || "Testimonials";
  const testimonialsSubtitle = home?.testimonials_subtitle || "See how educators and school leaders are transforming teaching through our professional development programmes.";

  const tCount = testimonials.length;

  return (
    <div style={{ color: C.ink }}>
      {/* Scoped font import + serif utility — local to this page only, not added to the
          shared stylesheet or Tailwind config, so no other page is affected. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&display=swap');
        .rebrand-serif { font-family: 'Playfair Display', Georgia, serif; }

        /* Partner Schools — pure CSS infinite scroll. Two copies of the same list are
           rendered (see .partners-copy-2 below); translating exactly -50% over the
           duration means the second copy lines up perfectly where the first began,
           so the loop restart is mathematically invisible — no JS, no reset, no jump. */
        @keyframes partners-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .partners-track {
          animation: partners-scroll 40s linear infinite;
          will-change: transform;
        }
        .partners-track:hover { animation-play-state: paused; }
        @media (max-width: 1024px) { .partners-track { animation-duration: 32s; } }
        @media (max-width: 640px) { .partners-track { animation-duration: 27s; } }
        @media (prefers-reduced-motion: reduce) {
          .partners-track { animation: none; transform: none; }
          .partners-copy-2 { display: none; }
        }

        /* Training Journey Timeline — pure CSS layout, no SVG. Desktop (lg+): 5 equal
           columns, each a vertical Circle -> Title -> Description Box stack. Below lg:
           horizontally scrollable, touch-swipeable, snap-scrolling row. Connector lines
           between nodes remain omitted (removed two rounds ago when the layout briefly
           went horizontal; this round reverts to vertical but doesn't reintroduce them
           since they weren't requested back). */
        .journey-row { display: flex; align-items: flex-start; justify-content: space-between; }
        .journey-item { position: relative; flex: 1; }
        .journey-node { transition: transform 0.2s ease; }
        .journey-node:hover { transform: scale(1.05); }
        .journey-box { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .journey-box:hover { transform: translateY(-4px); box-shadow: 0 12px 30px rgba(0,0,0,0.06); }
        @media (max-width: 1023px) {
          .journey-row {
            flex-wrap: nowrap; justify-content: flex-start; overflow-x: auto;
            scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch;
            scrollbar-width: none; -ms-overflow-style: none; gap: 32px;
            padding: 4px 24px 12px;
          }
          .journey-row::-webkit-scrollbar { display: none; }
          .journey-item { flex: 0 0 auto; width: 220px; scroll-snap-align: start; }
        }
        @media (prefers-reduced-motion: reduce) {
          .journey-node:hover { transform: none; }
          .journey-box:hover { transform: none; }
        }

        /* Testimonials carousel — native scroll container (drag/swipe/wheel work natively,
           no custom pointer JS needed); scrollbar hidden since the pagination dots and
           visible drag affordance already communicate scrollability. */
        .testimonials-scroller { scrollbar-width: none; -ms-overflow-style: none; }
        .testimonials-scroller::-webkit-scrollbar { display: none; }
        .testimonials-card { width: 85vw; max-width: 360px; }
        @media (min-width: 640px) { .testimonials-card { width: calc(50% - 14px); max-width: none; } }
        @media (min-width: 1024px) { .testimonials-card { width: calc(33.333% - 19px); } }
        @media (prefers-reduced-motion: reduce) {
          .testimonials-card:hover { transform: none; }
        }
      `}</style>

      {/* HERO — light, glass badges over a CMS-driven illustration/photo */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(59,104,231,0.08),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(69,184,172,0.08),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — copy */}
            <div>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6"
                style={ts(home, "hero_eyebrow", { backgroundColor: `${C.blue}14`, color: C.blue })}
              >
                {eyebrow}
              </motion.div>

              <motion.h1
                variants={fadeUp} initial="hidden" animate="show" custom={1}
                className="rebrand-serif text-5xl sm:text-6xl leading-[1.05] tracking-tight"
                style={ts(home, "hero_title")}
                data-testid="hero-headline"
              >
                <span style={{ color: C.blue }}>{h1}</span><br />
                <span style={{ color: C.ink }}>{h2}</span>
              </motion.h1>

              <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-6 text-lg leading-relaxed max-w-lg" style={ts(home, "hero_subtitle", { color: C.muted })}>
                {desc}
              </motion.p>

              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={ctaPrimary.link} data-testid="hero-cta-book"
                  className="group inline-flex items-center gap-2 text-white text-sm font-semibold px-6 py-3.5 rounded-full transition-transform hover:scale-[1.03]"
                  style={ts(home, "hero_cta_primary", { backgroundColor: C.blue, boxShadow: `0 12px 32px ${C.blue}33` })}
                >
                  {ctaPrimary.text} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link
                  to={ctaSecondary.link} data-testid="hero-cta-programs"
                  className="inline-flex items-center gap-2 bg-transparent text-sm font-semibold px-6 py-3.5 rounded-full border-2 transition-transform hover:scale-[1.03]"
                  style={ts(home, "hero_cta_secondary", { borderColor: C.gold, color: C.gold })}
                >
                  {ctaSecondary.text}
                </Link>
              </motion.div>
            </div>

            {/* Right — illustration with dynamic KPI glass badges */}
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-[24px] overflow-hidden shadow-xl aspect-[4/3]"
              >
                <img src={heroImage} alt="" className="w-full h-full object-cover" data-testid="hero-image" />
              </motion.div>

              {/* Dynamic KPI badges — all styles and positions CMS-driven */}
              {(() => {
                const activeKpis = stats
                  .filter((s) => s.visible !== false)
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                // Default positions for 1–6 KPIs used when no custom position is set
                const DEFAULT_POSITIONS_CSS = [
                  { top: "12px",  left: "-32px"                                     },
                  { top: "50%",   right: "-32px",  transform: "translateY(-50%)"    },
                  { bottom: "-24px", left: "25%"                                    },
                  { top: "33%",   right: "-32px"                                    },
                  { bottom: "-24px", right: "25%"                                   },
                  { top: "12px",  right: "25%"                                      },
                ];

                return activeKpis.slice(0, 6).map((kpi, i) => {
                  // Icon — custom URL takes priority over lucide name
                  const KpiIcon = KPI_ICON_MAP[kpi.icon] || Star;
                  const badgeBg = kpi.gradient || `linear-gradient(135deg, ${C.blue}E6 0%, ${C.blue}99 100%)`;

                  // Glass & shadow
                  const glassEnabled = kpi.glassEnabled !== false;
                  const glassBlur    = kpi.glassBlur ?? 12;
                  const borderColor  = kpi.glassBorderColor ?? "rgba(255,255,255,0.4)";
                  const borderWidth  = kpi.glassBorderWidth ?? 1;
                  const shadowColor  = kpi.shadowColor ?? "rgba(0,0,0,0.12)";
                  const shadowBlur   = kpi.shadowBlur ?? 32;
                  const shadowSpread = kpi.shadowSpread ?? 0;
                  const shadowOX     = kpi.shadowOffsetX ?? 0;
                  const shadowOY     = kpi.shadowOffsetY ?? 12;
                  const boxShadow    = `${shadowOX}px ${shadowOY}px ${shadowBlur}px ${shadowSpread}px ${shadowColor}`;

                  // Card dimensions
                  const cardW  = kpi.cardWidth ?? 160;
                  const cardR  = kpi.cardBorderRadius ?? 24;
                  const cardPX = kpi.cardPaddingX ?? 16;
                  const cardPY = kpi.cardPaddingY ?? 16;

                  // Icon sizing
                  const iconContainerSize = kpi.iconContainerSize ?? 28;
                  const iconSize          = kpi.iconSize ?? 14;
                  const iconRadius        = kpi.iconBorderRadius ?? 8;

                  // Hover
                  const hoverScale = kpi.hoverScale ?? 1.04;
                  const hoverLift  = kpi.hoverLift ?? -4;
                  const hoverGlow  = kpi.hoverGlow ?? false;
                  const glowColor  = badgeBg.includes("#") ? badgeBg.match(/#[0-9A-Fa-f]{6}/)?.[0] : null;

                  // Position — CMS-driven; falls back to default position by index
                  const hasCustomPos = kpi.posTop != null || kpi.posLeft != null ||
                                       kpi.posRight != null || kpi.posBottom != null;
                  const defaultPos   = DEFAULT_POSITIONS_CSS[i] || DEFAULT_POSITIONS_CSS[i % DEFAULT_POSITIONS_CSS.length];

                  // Build position style object (desktop default, responsive via inline style tag)
                  const posStyle = hasCustomPos ? {
                    top:    kpi.posTop    != null ? kpi.posTop    : undefined,
                    left:   kpi.posLeft   != null ? kpi.posLeft   : undefined,
                    right:  kpi.posRight  != null ? kpi.posRight  : undefined,
                    bottom: kpi.posBottom != null ? kpi.posBottom : undefined,
                  } : defaultPos;

                  // Extra transforms: rotation, scale (card-level, separate from hover)
                  const cardRotation = kpi.cardRotation ?? 0;
                  const cardScale    = kpi.cardScale ?? 1;
                  const extraTransform = [
                    posStyle.transform || "",
                    cardRotation !== 0 ? `rotate(${cardRotation}deg)` : "",
                    cardScale !== 1    ? `scale(${cardScale})`         : "",
                    kpi.posOffsetX || kpi.posOffsetY
                      ? `translate(${kpi.posOffsetX ?? 0}px, ${kpi.posOffsetY ?? 0}px)` : "",
                  ].filter(Boolean).join(" ") || undefined;

                  // Per-card z-index
                  const zIdx = kpi.zIndex ?? undefined;

                  // Tablet/mobile position overrides — emit a <style> tag if configured
                  const hasTabletPos = kpi.posTablet && Object.keys(kpi.posTablet).length > 0;
                  const hasMobilePos = kpi.posMobile && Object.keys(kpi.posMobile).length > 0;
                  const cardId = `kpi-badge-${kpi.id || i}`;

                  // Device visibility — default: desktop ✓, tablet ✓, mobile ✗
                  const desktopVisible = kpi.desktop_visible !== false;
                  const tabletVisible  = kpi.tablet_visible  !== false;
                  const mobileVisible  = kpi.mobile_visible  === true;

                  const buildResponsiveCss = () => {
                    let css = "";
                    // Desktop hide (>1023px) — rare but supported
                    if (!desktopVisible) {
                      css += `@media(min-width:1024px){#${cardId}{display:none!important;}}`;
                    }
                    // Tablet hide/position (≤1023px)
                    if (!tabletVisible) {
                      css += `@media(max-width:1023px){#${cardId}{display:none!important;}}`;
                    } else if (hasTabletPos) {
                      const t = kpi.posTablet;
                      css += `@media(max-width:1023px){#${cardId}{`;
                      if (t.top    != null) css += `top:${t.top};`;
                      if (t.left   != null) css += `left:${t.left};`;
                      if (t.right  != null) css += `right:${t.right};`;
                      if (t.bottom != null) css += `bottom:${t.bottom};`;
                      css += `}}`;
                    }
                    // Mobile hide/position (≤639px) — default hidden unless mobile_visible=true
                    if (!mobileVisible) {
                      css += `@media(max-width:639px){#${cardId}{display:none!important;}}`;
                    } else if (hasMobilePos) {
                      const m = kpi.posMobile;
                      css += `@media(max-width:639px){#${cardId}{`;
                      if (m.top    != null) css += `top:${m.top};`;
                      if (m.left   != null) css += `left:${m.left};`;
                      if (m.right  != null) css += `right:${m.right};`;
                      if (m.bottom != null) css += `bottom:${m.bottom};`;
                      css += `}}`;
                    }
                    return css || null;
                  };
                  const responsiveCss = buildResponsiveCss();

                  // Icon: custom URL takes priority over lucide icon
                  const iconEl = kpi.iconUrl ? (
                    <img src={kpi.iconUrl.startsWith("http") ? kpi.iconUrl : `${BACKEND}${kpi.iconUrl}`}
                      alt="" style={{ width: iconSize, height: iconSize, objectFit: "contain" }} />
                  ) : (
                    <KpiIcon style={{ width: iconSize, height: iconSize, color: kpi.iconColor || "#FFFFFF" }} />
                  );

                  return (
                    <React.Fragment key={kpi.id || i}>
                      {responsiveCss && <style>{responsiveCss}</style>}
                      <motion.div
                        id={cardId}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{
                          scale: hoverScale,
                          y: hoverLift,
                          boxShadow: hoverGlow && glowColor
                            ? `${shadowOX}px ${shadowOY}px ${shadowBlur + 16}px ${shadowSpread}px ${glowColor}66, 0 0 24px 4px ${glowColor}44`
                            : `${shadowOX}px ${shadowOY + 4}px ${shadowBlur + 8}px ${shadowSpread}px ${shadowColor}`,
                        }}
                        transition={{ duration: 0.7, delay: 0.4 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute text-white"
                        style={{
                          ...posStyle,
                          transform: extraTransform,
                          zIndex: zIdx,
                          width: cardW,
                          background: badgeBg,
                          backdropFilter: glassEnabled ? `blur(${glassBlur}px)` : undefined,
                          WebkitBackdropFilter: glassEnabled ? `blur(${glassBlur}px)` : undefined,
                          border: `${borderWidth}px solid ${borderColor}`,
                          borderRadius: cardR,
                          padding: `${cardPY}px ${cardPX}px`,
                          boxShadow,
                        }}
                        data-testid={`hero-badge-${i}`}
                      >
                        {/* Icon — above or below based on kpi.iconPosition */}
                        {kpi.iconPosition !== "bottom" && (
                          <div style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: iconContainerSize, height: iconContainerSize,
                            borderRadius: iconRadius, marginBottom: 8,
                            background: kpi.iconBg || "rgba(255,255,255,0.2)",
                            boxShadow: kpi.iconShadow || undefined,
                            padding: kpi.iconPadding || 0,
                          }}>
                            {iconEl}
                          </div>
                        )}

                        {/* Number */}
                        <div style={{
                          fontSize: kpi.numberFontSize ?? 24,
                          fontWeight: kpi.numberFontWeight ?? 600,
                          color: kpi.numberColor || "#FFFFFF",
                          fontFamily: kpi.numberFontFamily || undefined,
                          letterSpacing: kpi.numberLetterSpacing != null ? `${kpi.numberLetterSpacing}em` : "-0.02em",
                          lineHeight: kpi.numberLineHeight ?? 1.2,
                          textTransform: kpi.numberTextTransform && kpi.numberTextTransform !== "none" ? kpi.numberTextTransform : undefined,
                          textShadow: kpi.numberTextShadow || undefined,
                          ...ts(home, "stat_number"),
                        }}>
                          <KpiCountUp kpi={kpi} />
                        </div>

                        {/* Label */}
                        <div style={{
                          fontSize: kpi.labelFontSize ?? 11,
                          fontWeight: kpi.labelFontWeight ?? 400,
                          color: kpi.labelColor || "rgba(255,255,255,0.9)",
                          fontFamily: kpi.labelFontFamily || undefined,
                          letterSpacing: kpi.labelLetterSpacing != null ? `${kpi.labelLetterSpacing}em` : undefined,
                          lineHeight: kpi.labelLineHeight ?? 1.3,
                          marginTop: 2,
                          ...ts(home, "stat_label"),
                        }}>
                          {kpi.label || kpi.v || ""}
                        </div>

                        {/* Subtitle (distinct from description — slightly larger, bolder) */}
                        {kpi.subtitle && (
                          <div style={{
                            fontSize: kpi.subtitleFontSize ?? 10,
                            fontWeight: kpi.subtitleFontWeight ?? 500,
                            color: kpi.subtitleColor || "rgba(255,255,255,0.8)",
                            marginTop: 2,
                            lineHeight: 1.3,
                            fontStyle: "italic",
                          }}>
                            {kpi.subtitle}
                          </div>
                        )}

                        {/* Optional description */}
                        {kpi.description && (
                          <div style={{
                            fontSize: kpi.descriptionFontSize ?? 9,
                            color: kpi.descriptionColor || "rgba(255,255,255,0.7)",
                            marginTop: 2,
                            lineHeight: 1.3,
                          }}>
                            {kpi.description}
                          </div>
                        )}

                        {/* Icon — bottom position variant */}
                        {kpi.iconPosition === "bottom" && (
                          <div style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: iconContainerSize, height: iconContainerSize,
                            borderRadius: iconRadius, marginTop: 8,
                            background: kpi.iconBg || "rgba(255,255,255,0.2)",
                            boxShadow: kpi.iconShadow || undefined,
                            padding: kpi.iconPadding || 0,
                          }}>
                            {iconEl}
                          </div>
                        )}
                      </motion.div>
                    </React.Fragment>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* Partner Schools — pure CSS infinite scroll, no arrows/dots/JS timers. Data source:
          home.partners (existing CMS field, already public via GET /cms/home, already
          editable at /admin/content → Home tab). See the final report for why this reuses
          the CMS mechanism already powering this section rather than the auth-gated
          Schools collection. */}
      <section className="py-20 overflow-hidden" style={{ backgroundColor: "#FAFBFC" }} data-testid="partner-schools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!home ? (
            <div className="animate-pulse flex flex-col items-center" data-testid="partner-schools-skeleton">
              <div className="h-8 w-56 bg-gray-200 rounded mb-10" />
              <div className="flex gap-6 w-full justify-center">
                {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-14 w-32 bg-gray-200 rounded-lg" />)}
              </div>
            </div>
          ) : partners.length === 0 ? (
            <p className="text-center text-sm" style={{ color: C.muted }}>No partner schools available.</p>
          ) : (
            <>
              <h2
                className="rebrand-serif text-center tracking-tight text-[28px] sm:text-[34px] lg:text-[40px] font-semibold"
                style={{ color: C.ink, marginBottom: 36 }}
              >
                Trained Schools
              </h2>

              <div className="relative w-full overflow-hidden" data-testid="partner-schools-scroller">
                <div className="partners-track flex gap-6 w-max">
                  {[0, 1].map((copy) => (
                    <div key={copy} className={`flex gap-6 ${copy === 1 ? "partners-copy-2" : ""}`} aria-hidden={copy === 1 || undefined}>
                      {partners.map((p, i) => (
                        <div
                          key={i}
                          className="h-[70px] w-[130px] sm:w-[150px] md:w-[165px] lg:w-[180px] shrink-0 rounded-xl border border-gray-100 bg-white flex items-center justify-center px-4 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 hover:scale-[1.03] transition-all duration-300 cursor-default"
                          role="img"
                          aria-label={p}
                          data-testid={copy === 0 ? `partner-logo-${i}` : undefined}
                        >
                          <span
                            className="rebrand-serif text-base font-semibold text-center leading-tight"
                            style={{ color: C.ink, maxHeight: 54 }}
                          >
                            {p}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

            </>
          )}
        </div>
      </section>

      {/* Featured Programs — themed gradient cards. Data: home.featured() flag on Program
          (added this round — the model previously had none, so "featured" was implicitly
          just "the 3 newest programs"; see final report). Falls back to newest-3 if no
          program is explicitly marked featured yet, so the section never looks empty
          immediately after this change ships. */}
      <section className="py-20" style={{ backgroundColor: C.surface }} data-testid="featured-programs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="rebrand-serif text-center tracking-tight text-[28px] sm:text-[34px] lg:text-[40px] font-semibold"
            style={{ color: C.ink, marginBottom: 48 }}
          >
            Featured Programs
          </h2>

          {programsLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="featured-programs-skeleton">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-[24px] h-72 bg-gray-200 animate-pulse" />
              ))}
            </div>
          ) : programsError ? (
            <p className="text-center text-sm" style={{ color: C.muted }}>Unable to load featured programs.</p>
          ) : programs.length === 0 ? (
            <p className="text-center text-sm" style={{ color: C.muted }}>No featured programs available.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7 items-stretch">
              {programs.map((p, i) => {
                const theme = themeFor(p.category, i);
                const Icon = iconFor(p.category);
                const coverImg = p.cover_image
                  ? (p.cover_image.startsWith("http") ? p.cover_image : `${BACKEND}${p.cover_image}`)
                  : null;
                return (
                  <motion.div key={p.id} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="h-full">
                    <Link
                      to={`/programs/${p.id}`}
                      data-testid={`home-program-${p.id}`}
                      aria-label={`Learn more about ${p.title}`}
                      className="block h-full rounded-[24px] flex flex-col transition-all duration-300 hover:-translate-y-1.5 overflow-hidden"
                      style={{ boxShadow: "0 12px 24px rgba(0,0,0,0.06)" }}
                    >
                      {/* Cover image — or gradient fallback */}
                      <div className="relative w-full shrink-0 overflow-hidden" style={{ height: 200 }}>
                        {coverImg ? (
                          <>
                            <img
                              src={coverImg}
                              alt={p.title}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            {/* Subtle dark overlay for text readability on category badge */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                          </>
                        ) : (
                          <div
                            className="w-full h-full flex items-end p-4"
                            style={{ background: `linear-gradient(180deg, ${theme.top} 0%, ${theme.bottom} 100%)` }}
                          >
                            <div
                              className="w-10 h-10 rounded-xl grid place-items-center"
                              style={{ backgroundColor: theme.iconBg }}
                            >
                              <Icon className="w-5 h-5" style={{ color: theme.iconColor }} aria-hidden="true" />
                            </div>
                          </div>
                        )}
                        {/* Category badge */}
                        {p.category && (
                          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-black/40 text-white backdrop-blur-sm">
                            {p.category}
                          </span>
                        )}
                        {p.featured && (
                          <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-white/90 text-[#1A202C]">
                            Featured
                          </span>
                        )}
                      </div>

                      {/* Card body */}
                      <div
                        className="flex flex-col flex-1 p-6"
                        style={{
                          background: coverImg
                            ? "#FFFFFF"
                            : `linear-gradient(180deg, ${theme.top} 0%, ${theme.bottom} 100%)`,
                        }}
                      >
                        <h3
                          className="text-xl sm:text-[22px] font-semibold leading-[1.3] line-clamp-2"
                          style={{ color: coverImg ? "#1A202C" : theme.title }}
                        >
                          {p.title}
                        </h3>
                        <p
                          className="mt-3 text-base leading-[1.6] line-clamp-3 flex-1"
                          style={{ color: coverImg ? "#4B5563" : theme.body }}
                        >
                          {p.subtitle || p.description}
                        </p>

                        {/* Meta row */}
                        {(p.duration || p.mode) && (
                          <div className="mt-3 flex items-center gap-3 flex-wrap">
                            {p.duration && (
                              <span className="text-xs font-medium" style={{ color: coverImg ? "#64748B" : theme.body }}>
                                ⏱ {p.duration}
                              </span>
                            )}
                            {p.mode && (
                              <span className="text-xs font-medium" style={{ color: coverImg ? "#64748B" : theme.body }}>
                                📍 {p.mode}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="mt-5">
                          <span
                            className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full hover:opacity-80 transition-opacity"
                            style={
                              coverImg
                                ? { backgroundColor: "#EEF2FF", color: "#4F46E5", padding: "10px 18px" }
                                : { backgroundColor: theme.ctaBg, color: theme.ctaText, padding: "10px 18px" }
                            }
                          >
                            Learn More <ArrowUpRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us — CMS-driven via home.why_choose_us (added this round; previously a
          hardcoded array with zero backend connection). PUT /cms/home already accepts an
          arbitrary dict, so this needed no new endpoint, no new model, no new collection —
          just a new key on the existing document, editable from the existing Home tab in
          admin/Content.jsx. Array position is display order (same convention already used
          by the About page's Timeline/Achievements editors); `visible` hides an item
          without deleting it. */}
      <section className="pt-[90px] pb-[90px]" style={{ backgroundColor: "#FFFFFF" }} data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="rebrand-serif text-center tracking-tight text-[28px] sm:text-[34px] lg:text-[40px] font-bold"
            style={ts(home, "section_heading", { color: "#1A202C", marginBottom: 60 })}
          >
            Why Choose Us
          </h2>

          {!home ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-[40px]" data-testid="why-choose-us-skeleton">
              {[0, 1, 2].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="w-12 h-12 rounded-full bg-gray-200 mb-4" />
                  <div className="h-4 w-32 bg-gray-200 rounded mb-2" />
                  <div className="h-3 w-full bg-gray-100 rounded" />
                </div>
              ))}
            </div>
          ) : whyChooseUsItems.length === 0 ? (
            <p className="text-center text-sm" style={{ color: "#64748B" }}>No features available.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-[40px]">
              {whyChooseUsItems.map((f, i) => {
                const Icon = WHY_ICON_MAP[f.icon] || BookOpen;
                return (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    custom={i}
                    className="group text-left transition-all duration-300 hover:-translate-y-[5px] hover:shadow-[0_10px_15px_rgba(0,0,0,0.05)] rounded-2xl p-2 -m-2"
                    data-testid={`why-choose-us-item-${i}`}
                  >
                    <div
                      className="w-12 h-12 rounded-full grid place-items-center mb-4"
                      style={{ backgroundColor: "#EEF2F6" }}
                    >
                      <Icon className="w-5 h-5" style={{ color: "#1A202C" }} strokeWidth={1.75} aria-hidden="true" />
                    </div>
                    <h3
                      className="font-semibold text-lg leading-[1.3] line-clamp-2"
                      style={ts(home, "why_card_title", { color: "#1A202C" })}
                    >
                      {f.title}
                    </h3>
                    <p
                      className="mt-2 text-[15px] line-clamp-3"
                      style={ts(home, "why_card_desc", { color: "#64748B", lineHeight: 1.5 })}
                    >
                      {f.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Training Journey Timeline — CMS-driven via home.training_journey (added this round,
          same PUT /cms/home arbitrary-dict mechanism as Why Choose Us and Partner Schools —
          no new endpoint, no new model). Array position is display order, matching the
          established convention. "Animation Delay" from the reference spec is deliberately
          NOT a stored field — it's derived from array position (index * 120ms), since
          storing a redundant delay value that could drift out of sync with position would
          be the kind of unnecessary field this brief itself asks to avoid. */}
      <section className="pt-[100px] pb-[100px]" style={{ backgroundColor: "#F4F7FB" }} data-testid="training-journey-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="rebrand-serif text-center tracking-tight text-[28px] sm:text-[34px] lg:text-[40px] font-bold"
            style={ts(home, "section_heading", { color: "#1A202C" })}
          >
            {journeyHeading}
          </motion.h2>
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="text-center mx-auto mt-3 text-[15px]"
            style={ts(home, "section_subtitle", { color: "#64748B", maxWidth: 650, marginBottom: 70 })}
          >
            {journeySubtitle}
          </motion.p>

          {!home ? (
            <div className="journey-row" data-testid="training-journey-skeleton">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="journey-item animate-pulse">
                  <div className="w-[72px] h-[72px] rounded-full bg-gray-200 mb-4" />
                  <div className="h-3 w-16 bg-gray-200 rounded mb-2" />
                  <div className="h-3 w-20 bg-gray-100 rounded" />
                </div>
              ))}
            </div>
          ) : trainingJourneySteps.length === 0 ? (
            <p className="text-center text-sm" style={{ color: "#64748B" }}>No timeline steps available.</p>
          ) : (
            <div className="journey-row" role="list" aria-label="Training journey steps">
              {trainingJourneySteps.map((step, i) => {
                const Icon = WHY_ICON_MAP[step.icon] || BookOpen;
                const light = isLightColor(step.color);
                const content = (
                  <div className="flex flex-col items-center text-center">
                    <div
                      className="journey-node relative z-10 rounded-full grid place-items-center shrink-0"
                      style={{ width: 70, height: 70, backgroundColor: step.color || "#2B4380" }}
                    >
                      <Icon style={{ width: 30, height: 30, color: light ? "#1A202C" : "#FFFFFF" }} strokeWidth={1.75} aria-hidden="true" />
                    </div>
                    <h3
                      className="font-bold text-lg leading-[1.3]"
                      style={ts(home, "journey_step_title", { color: "#1A202C", marginTop: 16, marginBottom: 12 })}
                    >
                      {step.title}
                    </h3>
                    {/* Description info box — fixed min-height/max-width regardless of text
                        length, so all five boxes stay identical in size; content is
                        vertically AND horizontally centered inside via flex, not just
                        text-align, so short descriptions don't sit awkwardly at the top. */}
                    <div
                      className="journey-box w-full flex items-center justify-center"
                      style={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E8EDF3",
                        borderRadius: 18,
                        padding: 20,
                        minHeight: 140,
                        maxWidth: 220,
                      }}
                    >
                      <p
                        className="text-[15px]"
                        style={ts(home, "journey_step_desc", { color: "#64748B", lineHeight: 1.6 })}
                      >
                        {step.description}
                      </p>
                    </div>
                    {step.link && step.button_text && (
                      <span className="mt-3 inline-block text-xs font-semibold" style={{ color: C.blue }}>{step.button_text}</span>
                    )}
                  </div>
                );
                return (
                  <motion.div
                    key={i}
                    role="listitem"
                    className="journey-item"
                    variants={{
                      hidden: { opacity: 0, scale: 0.85 },
                      show: { opacity: 1, scale: 1, transition: { duration: 0.5, delay: i * 0.12 } },
                    }}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-40px" }}
                    data-testid={`journey-step-${i}`}
                  >
                    {step.link ? (
                      <a href={step.link} className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 rounded-xl" style={{ outlineColor: C.blue }} aria-label={`${step.title} — ${step.description}`}>
                        {content}
                      </a>
                    ) : (
                      <div aria-label={`Step ${i + 1}: ${step.title} — ${step.description}`}>{content}</div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials — auto-scrolling carousel. Native horizontal scroll (drag/swipe/wheel
          all work for free) + a rAF loop nudging scrollLeft for the "auto-scroll" effect;
          see the effect above for the full seamless-loop mechanism. */}
      <section style={{ backgroundColor: "#FFFFFF", paddingTop: 100, paddingBottom: 110 }} data-testid="testimonials-section">
        <div className="mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: 1320 }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mx-auto" style={{ marginBottom: 60 }}>
            <h2 className="rebrand-serif text-[28px] sm:text-[34px] lg:text-[42px] font-semibold tracking-tight" style={ts(home, "section_heading", { color: "#1A202C" })}>
              {testimonialsHeading}
            </h2>
            <p className="mt-4 mx-auto text-base leading-relaxed" style={ts(home, "section_subtitle", { color: "#64748B", maxWidth: 650 })}>
              {testimonialsSubtitle}
            </p>
          </motion.div>

          {testimonialsLoading ? (
            <div className="flex overflow-hidden" style={{ gap: 28 }} data-testid="testimonials-skeleton">
              {[0, 1, 2].map((i) => (
                <div key={i} className="testimonials-card bg-white animate-pulse shrink-0" style={{ borderRadius: 22, padding: 30, height: 280 }}>
                  <div className="h-4 w-24 bg-gray-100 rounded mb-4" />
                  <div className="h-3 w-full bg-gray-100 rounded mb-2" />
                  <div className="h-3 w-3/4 bg-gray-100 rounded mb-6" />
                  <div className="flex items-center gap-3 mt-auto">
                    <div className="w-12 h-12 rounded-full bg-gray-100" />
                    <div className="h-3 w-20 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : tCount === 0 ? (
            <div className="text-center py-10" data-testid="testimonials-empty">
              <Quote className="w-8 h-8 mx-auto mb-3" style={{ color: "#CBD5E1" }} />
              <p className="text-sm" style={{ color: "#64748B" }}>No testimonials have been published yet.</p>
            </div>
          ) : (
            <div
              ref={tScrollerRef}
              onScroll={onTScroll}
              onMouseEnter={pauseTCarousel}
              onMouseLeave={resumeTCarouselSoon}
              onTouchStart={pauseTCarousel}
              onTouchEnd={resumeTCarouselSoon}
              onWheel={() => { pauseTCarousel(); resumeTCarouselSoon(); }}
              onKeyDown={onTKeyDown}
              tabIndex={0}
              role="region"
              aria-label="Testimonials carousel — use arrow keys to navigate"
              className="testimonials-scroller flex overflow-x-auto"
              style={{ gap: 28, scrollSnapType: "x mandatory" }}
              data-testid="testimonials-scroller"
            >
              {[0, 1].flatMap((copy) =>
                testimonials.map((t, i) => {
                  const expanded = expandedQuotes.has(`${copy}-${t.id}`);
                  return (
                    <motion.figure
                      key={`${copy}-${t.id}`}
                      variants={fadeUp}
                      initial="hidden"
                      whileInView={copy === 0 ? "show" : undefined}
                      viewport={{ once: true }}
                      custom={i}
                      aria-hidden={copy === 1 || undefined}
                      className="testimonials-card group relative bg-white flex flex-col shrink-0 hover:-translate-y-1.5 transition-all duration-300"
                      style={{
                        borderRadius: 22,
                        boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
                        padding: 30,
                        scrollSnapAlign: "start",
                      }}
                      data-testid={copy === 0 ? `testimonial-${t.id}` : undefined}
                    >
                      <Quote
                        className="absolute top-4 left-4 pointer-events-none group-hover:opacity-[0.08] transition-opacity"
                        style={{ width: 120, height: 120, color: "#3B68E7", opacity: 0.05 }}
                        aria-hidden="true"
                      />

                      <div className="relative flex gap-0.5 mb-4">
                        {Array.from({ length: t.rating || 5 }).map((_, si) => (
                          <Star key={si} className="w-4 h-4 transition-transform group-hover:scale-110" style={{ color: "#F4B740", fill: "#F4B740", transitionDelay: `${si * 30}ms` }} />
                        ))}
                      </div>

                      <blockquote
                        className={`relative flex-1 text-base ${expanded ? "" : "line-clamp-4"}`}
                        style={{ color: "#475569", lineHeight: 1.8 }}
                      >
                        &ldquo;{t.quote}&rdquo;
                      </blockquote>
                      {t.quote && t.quote.length > 180 && (
                        <button
                          onClick={() => toggleQuoteExpanded(`${copy}-${t.id}`)}
                          className="relative mt-2 text-xs font-semibold self-start hover:underline"
                          style={{ color: "#3B68E7" }}
                        >
                          {expanded ? "Show less" : "Read more"}
                        </button>
                      )}

                      {(t.verified || t.role) && (
                        <div className="relative mt-4 flex flex-wrap gap-1.5">
                          {t.verified && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: "#ECFDF5", color: "#059669" }}>
                              <CheckCircle2 className="w-3 h-3" /> Verified School
                            </span>
                          )}
                          {t.role && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: "#EEF2FF", color: "#4F46E5" }}>
                              {t.role}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="relative mt-5 pt-4 border-t border-gray-100 flex items-center gap-3">
                        {t.photo ? (
                          <img
                            src={resolveTrainerImage(t.photo)}
                            alt={t.author}
                            loading="lazy"
                            width={48}
                            height={48}
                            className="w-12 h-12 rounded-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full grid place-items-center text-white font-semibold text-sm shrink-0" style={{ backgroundColor: "#45B8AC" }}>
                            {trainerInitials(t.author)}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-base font-semibold truncate" style={ts(home, "testimonial_author", { color: "#1A202C" })}>{t.author}</div>
                          {t.role && <div className="text-sm truncate" style={ts(home, "testimonial_role", { color: "#64748B" })}>{t.role}</div>}
                          {t.school && <div className="text-[13px] truncate" style={{ color: "#94A3B8" }}>{t.school}</div>}
                        </div>
                      </div>
                    </motion.figure>
                  );
                })
              )}
            </div>
          )}

          {!testimonialsLoading && tCount > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              {testimonials.map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => scrollTToIndex(i)}
                  aria-label={`Go to testimonial ${i + 1} of ${tCount}`}
                  aria-current={i === tActive}
                  className="rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: i === tActive ? "#3B68E7" : "#CBD5E1",
                    width: i === tActive ? 24 : 8,
                    height: 8,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Meet the Experts — CMS-driven, sourced from /api/cms/members (unchanged data
          source; this round added `subjects`/`website` as two small new optional fields on
          the same arbitrary-dict member documents — no new collection). Heading/subtitle
          now CMS-editable via home.meet_experts_heading/subtitle, same fallback pattern as
          the other Home sections. Deliberately NOT using a fabricated "fallback trainers"
          list if the team roster is empty — inventing fictional staff to fill space would
          be dishonest in a way the other sections' generic marketing-copy fallbacks
          weren't; the existing soft empty message is kept instead. */}
      <TrainersSection
        eyebrow="The team"
        title={home?.meet_experts_heading || "Meet the Experts"}
        description={home?.meet_experts_subtitle || "Experienced educators, pedagogy researchers, school leaders and master trainers helping schools transform teaching and learning."}
        variant="default"
        limit={8}
        columns="md:grid-cols-2 lg:grid-cols-4"
        gap="gap-8"
        centerHeader
        showViewAll
        testid="home-trainers-section"
      />

      {/* Certificate Verification — now a genuine inline verification form (previous round
          only linked out to /verify). Reuses the exact same GET /verify/{id} endpoint and
          response handling as Verify.jsx, just embedded here per this round's brief.
          QR still comes from the real /api/qr/verify endpoint built two rounds ago. */}
      {verifyCtaActive && (
        <section className="py-20" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F4F7FB 100%)" }} data-testid="verify-cta-section">
          <div className="mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="relative overflow-hidden bg-white grid lg:grid-cols-5 gap-10 lg:gap-12 transition-shadow duration-300 hover:shadow-[0_24px_60px_rgba(15,23,42,0.08)]"
              style={{
                borderRadius: 28,
                border: "1px solid #E8EDF3",
                padding: 64,
                boxShadow: "0 20px 50px rgba(15,23,42,0.06)",
              }}
              data-testid="verify-cta-card"
            >
              {/* Left (60%) */}
              <div className="lg:col-span-3">
                <h2 className="rebrand-serif text-[32px] sm:text-[42px] font-bold leading-[1.1]" style={ts(home, "verify_heading", { color: "#1A202C" })}>
                  {verifyCtaHeading || "Verify Your Certificate"}
                </h2>
                <p className="mt-4 text-base leading-relaxed" style={ts(home, "verify_desc", { color: "#64748B", maxWidth: 480 })}>
                  {verifyCtaDescription || "Instantly verify teacher certificates issued through our training platform using a secure Certificate ID or QR code."}
                </p>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} aria-hidden="true" />
                    <input
                      value={verifyQuery}
                      onChange={(e) => setVerifyQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && doVerifyInline()}
                      placeholder="Enter Certificate ID"
                      aria-label="Certificate ID"
                      data-testid="verify-cta-input"
                      className="w-full pl-11 pr-4 rounded-[14px] border outline-none text-sm font-mono focus:ring-4 transition-all"
                      style={{ height: 54, borderColor: "#DCE4EC", "--tw-ring-color": "#3B68E71A" }}
                    />
                  </div>
                  <button
                    onClick={doVerifyInline}
                    disabled={verifyLoading}
                    data-testid="verify-cta-submit"
                    className="text-white text-sm font-semibold rounded-[14px] shrink-0 inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] disabled:opacity-60"
                    style={{ height: 54, paddingLeft: 28, paddingRight: 28, backgroundColor: "#3B68E7" }}
                  >
                    {verifyLoading ? (
                      <>
                        <span className="w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Checking…
                      </>
                    ) : (
                      "Verify Certificate"
                    )}
                  </button>
                </div>

                {/* Trust indicators */}
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
                  {["QR Verified", "Secure Database", "Instant Verification", "Digitally Authenticated"].map((label) => (
                    <span key={label} className="inline-flex items-center gap-1.5 text-xs" style={{ color: "#64748B" }}>
                      <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#3FB39E" }} aria-hidden="true" /> {label}
                    </span>
                  ))}
                </div>

                {/* Result */}
                <AnimatePresence mode="wait">
                  {verifyResult && (
                    <motion.div
                      key={verifyResult.valid ? "valid" : "invalid"}
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      className="mt-6 rounded-2xl p-5"
                      style={{ backgroundColor: verifyResult.valid ? "#ECFDF5" : "#FEF2F2", border: `1px solid ${verifyResult.valid ? "#A7F3D0" : "#FECACA"}` }}
                      data-testid="verify-cta-result"
                    >
                      {verifyResult.valid ? (
                        <div className="flex gap-3">
                          <CheckCircle2 className="w-6 h-6 shrink-0" style={{ color: "#059669" }} />
                          <div className="min-w-0">
                            <div className="font-semibold text-sm" style={{ color: "#059669" }}>This certificate is valid</div>
                            <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs" style={{ color: "#374151" }}>
                              <div><span style={{ color: "#6B7280" }}>Teacher:</span> {verifyResult.certificate.teacher_name}</div>
                              <div><span style={{ color: "#6B7280" }}>School:</span> {verifyResult.certificate.school_name}</div>
                              <div><span style={{ color: "#6B7280" }}>Training:</span> {verifyResult.certificate.training_name}</div>
                              <div><span style={{ color: "#6B7280" }}>Date:</span> {verifyResult.certificate.date}</div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-3">
                          <XCircle className="w-6 h-6 shrink-0" style={{ color: "#DC2626" }} />
                          <div>
                            <div className="font-semibold text-sm" style={{ color: "#DC2626" }}>Certificate not found</div>
                            <div className="text-xs mt-1" style={{ color: "#6B7280" }}>Check the ID and try again.</div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Right (40%) — trust illustration */}
              <div className="lg:col-span-2 relative flex items-center justify-center min-h-[280px]">
                {/* Soft floating decorative shapes, behind everything */}
                <div className="absolute w-56 h-56 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(59,104,231,0.08) 0%, transparent 70%)", top: -20, right: -20 }} />
                <div className="absolute w-40 h-40 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(69,184,172,0.10) 0%, transparent 70%)", bottom: -10, left: 0 }} />

                {/* Certificate mockup card, tilted behind the QR */}
                <motion.div
                  className="absolute bg-white rounded-2xl p-4"
                  style={{ width: 150, border: "1px solid #E8EDF3", boxShadow: "0 12px 30px rgba(15,23,42,0.08)", transform: "rotate(-8deg) translate(-64px, 12px)" }}
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Award className="w-5 h-5 mb-2" style={{ color: "#3B68E7" }} aria-hidden="true" />
                  <div className="h-1.5 w-full rounded-full mb-1.5" style={{ backgroundColor: "#E8EDF3" }} />
                  <div className="h-1.5 w-3/4 rounded-full mb-1.5" style={{ backgroundColor: "#E8EDF3" }} />
                  <div className="h-1.5 w-1/2 rounded-full" style={{ backgroundColor: "#E8EDF3" }} />
                </motion.div>

                {/* Real QR code, gently floating */}
                <motion.div
                  className="relative bg-white z-10"
                  style={{ borderRadius: 20, padding: 16, border: "1px solid #E8EDF3", boxShadow: "0 16px 40px rgba(15,23,42,0.10)" }}
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <img
                    src={`${BACKEND}/api/qr/verify`}
                    alt="QR code linking to the certificate verification page"
                    loading="lazy"
                    width={140}
                    height={140}
                    data-testid="verify-cta-qr"
                  />
                  {/* Verified check, overlapping the QR corner */}
                  <div
                    className="absolute rounded-full grid place-items-center"
                    style={{ width: 36, height: 36, backgroundColor: "#3FB39E", bottom: -10, right: -10, border: "3px solid white" }}
                    aria-label="Verified"
                  >
                    <CheckCircle2 className="w-4 h-4 text-white" aria-hidden="true" />
                  </div>
                </motion.div>

                {/* Secure shield badge, floating opposite corner */}
                <motion.div
                  className="absolute rounded-full grid place-items-center"
                  style={{ width: 56, height: 56, backgroundColor: "#2D7A73", top: 8, right: 24, boxShadow: "0 8px 20px rgba(45,122,115,0.35)" }}
                  animate={{ y: [0, 7, 0] }}
                  transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  aria-label="Secure verification"
                >
                  <ShieldCheck className="w-6 h-6 text-white" aria-hidden="true" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Achievements */}
      <AchievementsSection />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="rounded-[32px] text-white p-10 sm:p-16 relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${C.blue} 0%, #2947B0 100%)` }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.12),transparent_55%)]" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <Sparkles className="w-6 h-6 mb-4" style={{ color: C.gold }} />
              <h2 className="rebrand-serif text-4xl sm:text-5xl tracking-tight" style={ts(home, "cta_heading")}>Ready to lift your teachers?</h2>
              <p className="mt-4 text-white/80 max-w-md">Book a discovery call. We&apos;ll design a custom training arc for your school in 48 hours.</p>
            </div>
            <div className="md:justify-self-end flex flex-wrap gap-3">
              <Link to="/book" data-testid="cta-book" className="bg-white hover:bg-white/90 text-sm font-semibold px-6 py-3.5 rounded-full inline-flex items-center gap-2 group" style={{ color: C.blue }}>
                Book a training <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/contact" data-testid="cta-contact" className="border-2 text-white hover:bg-white/10 text-sm font-semibold px-6 py-3.5 rounded-full" style={{ borderColor: C.gold }}>Contact sales</Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
